import React, { useState, useEffect, useRef } from 'react';
import { usersService } from '../services/users.service';
import { useAuth } from '../context/AuthContext';
import { User } from '../types';
import Header from '../components/Layout/Header';
import MobileSidebar from '../components/Layout/mobileSidebar';
import Button from '../components/Button';
import Combobox from '../components/Combobox';
import { isCoAdminOrAdmin, isAdminRole, formatRoleLabel } from '../utils/roles';
import './UserManagement.css';

const MOBILE_TABLET_BREAKPOINT = 1024; // px

const ROLE_OPTIONS = [
  'EMPLOYEE',
  'BRANCH_ADMIN',
  'DEPARTMENT_ADMIN',
  'CONTENT_UPLOADER',
  'CO_ADMIN',
  'ADMIN',
] as const;

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('ALL');
  const [filterRole, setFilterRole] = useState('ALL');
  const [isImporting, setIsImporting] = useState(false);
  const [showImportSection, setShowImportSection] = useState(false);
  type FeedbackMessage = {
    kind: 'success' | 'warning' | 'error';
    title: string;
    details?: string[];
  } | null;
  const [feedback, setFeedback] = useState<FeedbackMessage>(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState<boolean>(
    () =>
      typeof window !== 'undefined'
        ? window.innerWidth <= MOBILE_TABLET_BREAKPOINT
        : true
  );

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingDeleteUserId, setPendingDeleteUserId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [shouldCloseOnBackdropMouseUp, setShouldCloseOnBackdropMouseUp] = useState(false);

  // Bulk selection + pagination
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [pendingBulkDelete, setPendingBulkDelete] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  // Form state
  const [formData, setFormData] = useState<{
    empNo: string;
    fullName: string;
    department: string;
    branch: string;
    password: string;
    email: string;
    telNo: string;
    designation: string;
    role: User['role'];
  }>({
    empNo: '',
    fullName: '',
    department: '',
    branch: '',
    password: '',
    email: '',
    telNo: '',
    designation: '',
    role: 'EMPLOYEE',
  });

  const departments = ['IT', 'HR', 'ADMIN', 'FINANCE', 'SALES', 'SERVICE'];
  const branches = [
    'Ampara',
    'Anuradhapura',
    'BPC Wattala',
    'Galle',
    'Gampaha',
    'Hino Centre - Wattala',
    'Hyde Park Corner',
    'Kalutara',
    'Kandy',
    'Kurunegala',
    'Maharagama',
    'Matara',
    'Negombo',
    'Nugegoda',
    'Panchikawatte',
    'Ratmalana',
    'Ratnapura',
    'Toyota Plaza - Wattala'
  ];
  const roles = [...ROLE_OPTIONS];
  const importInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    const isModalOpen = showModal || !!pendingDeleteUserId || pendingBulkDelete;
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [showModal, pendingDeleteUserId, pendingBulkDelete]);

  // Reset to page 1 whenever filters or search change.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterDepartment, filterRole]);

  // Auto-dismiss success banners after a short delay; warnings/errors stay
  // until the user dismisses them so they don't miss the detail list.
  useEffect(() => {
    if (feedback?.kind === 'success') {
      const t = window.setTimeout(() => setFeedback(null), 6000);
      return () => window.clearTimeout(t);
    }
  }, [feedback]);

  // keep filterUsers stable and run when its inputs change
  const filterUsers = React.useCallback(() => {
    let filtered = [...users];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          u.fullName.toLowerCase().includes(term) ||
          u.empNo.toLowerCase().includes(term)
      );
    }

    // Department filter
    if (filterDepartment !== 'ALL') {
      filtered = filtered.filter((u) => u.department === filterDepartment);
    }

    // Role filter
    if (filterRole !== 'ALL') {
      filtered = filtered.filter((u) => u.role === filterRole);
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, filterDepartment, filterRole]);

  useEffect(() => {
    filterUsers();
  }, [filterUsers]);

  useEffect(() => {
    const onResize = () =>
      setIsMobileOrTablet(window.innerWidth <= MOBILE_TABLET_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const loadUsers = async () => {
    try {
      const data = await usersService.getAll();
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidPhoneNumber = (phone: string) => {
    return /^(?:0\d{9}|94\d{9}|\+94\d{9})$/.test(phone);
  };

  const parseCSVLine = (line: string) => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
        continue;
      }

      if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    values.push(current.trim());
    return values;
  };

  const handleImportClick = () => {
    setShowImportSection(true);
    window.setTimeout(() => importInputRef.current?.click(), 0);
  };

  const handleDownloadTemplate = async () => {
    setFeedback(null);
    try {
      await usersService.downloadImportTemplate();
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to download CSV template.';
      setFeedback({ kind: 'error', title: message });
    }
  };

  const handleImportCSV = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFeedback(null);
    setIsImporting(true);

    try {
      // Refresh users to get the latest state before duplicate checks
      const latestUsers = await usersService.getAll();
      setUsers(latestUsers);

      const text = await file.text();
      const lines = text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);

      if (lines.length < 2) {
        throw new Error('CSV must contain a header row and at least one user row.');
      }

      const normalizeHeader = (h: string) => h.toLowerCase().replace(/[\s_]/g, '');
      const headersRaw = parseCSVLine(lines[0]);
      const headers = headersRaw.map(normalizeHeader);
      const headerIndex: Record<string, number> = {};
      headers.forEach((h, idx) => { headerIndex[h] = idx; });

      // Expected format (case/spacing insensitive):
      // empNo, fullName, department, branch, password, role, USER EMAIL, DESIGNATION, MOBILE
      const requiredHeaders = ['empno', 'fullname', 'department', 'branch', 'password', 'role'];
      const missingHeaders = requiredHeaders.filter((h) => headerIndex[h] === undefined);
      if (missingHeaders.length > 0) {
        throw new Error(
          `Missing required headers: ${missingHeaders.join(', ')}. Expected columns: Emp No, Full Name, Department, Branch, Password, Role. Optional: User Email, Designation, Mobile.`
        );
      }

      const getValue = (row: string[], key: string) => {
        const index = headerIndex[key];
        return index !== undefined ? row[index]?.trim() || '' : '';
      };

      const parsedUsers = lines.slice(1).map((line, idx) => {
        const rowNumber = idx + 2;
        const columns = parseCSVLine(line);
        let role = getValue(columns, 'role').toUpperCase();
        if (role === 'HR_ADMIN') role = 'CO_ADMIN';
        const rawEmail = getValue(columns, 'useremail');
        const safeEmail = rawEmail && isValidEmail(rawEmail) ? rawEmail : '';
        const rawMobile = getValue(columns, 'mobile');
        const designation = getValue(columns, 'designation');

        if (!requiredHeaders.every((field) => getValue(columns, field))) {
          throw new Error(`Row ${rowNumber}: one or more required fields are empty.`);
        }

        if (!roles.includes(role as any)) {
          throw new Error(`Row ${rowNumber}: role must be one of ${roles.join(', ')}.`);
        }

        return {
          rowNumber,
          payload: {
            empNo: getValue(columns, 'empno'),
            fullName: getValue(columns, 'fullname'),
            department: getValue(columns, 'department'),
            branch: getValue(columns, 'branch'),
            password: getValue(columns, 'password'),
            email: safeEmail || undefined,
            telNo: rawMobile || undefined,
            designation: designation || undefined,
            role: role as User['role'],
          },
        };
      });

      let importedCount = 0;
      let skippedDuplicates = 0;

      // Only enforce unique emails (backend still has unique on email). empNo is allowed to duplicate.
      const existingEmails = new Set(
        latestUsers
          .map((u) => u.email?.toLowerCase())
          .filter((e): e is string => !!e)
      );
      const seenEmails = new Set<string>();

      for (const { rowNumber, payload } of parsedUsers) {
        const emailKey = payload.email ? payload.email.toLowerCase() : '';

        // Skip if email is present and already exists (db or earlier in file)
        if (emailKey && (existingEmails.has(emailKey) || seenEmails.has(emailKey))) {
          skippedDuplicates++;
          continue;
        }

        try {
          await usersService.create(payload);
          importedCount++;
          if (emailKey) seenEmails.add(emailKey);
        } catch (err: any) {
          // Skip duplicate conflicts (email) and continue
          if (err?.response?.status === 409) {
            skippedDuplicates++;
            continue;
          }
          // Surface the backend's actual validation message + which row failed.
          const serverMsg = err?.response?.data?.message;
          const detail = Array.isArray(serverMsg)
            ? serverMsg.join('; ')
            : serverMsg || err?.message || 'Unknown error';
          throw new Error(`Row ${rowNumber}: ${detail}`);
        }
      }

      await loadUsers();

      // Build a single structured feedback message rather than alert + inline.
      const userWord = (n: number) => `${n} user${n === 1 ? '' : 's'}`;
      const rowWord = (n: number) => `${n} duplicate row${n === 1 ? '' : 's'}`;
      if (importedCount > 0 && skippedDuplicates === 0) {
        setFeedback({
          kind: 'success',
          title: `Successfully imported ${userWord(importedCount)}.`,
        });
      } else if (importedCount > 0 && skippedDuplicates > 0) {
        setFeedback({
          kind: 'warning',
          title: `Imported ${userWord(importedCount)} — skipped ${rowWord(
            skippedDuplicates,
          )} (duplicate email).`,
        });
      } else if (importedCount === 0 && skippedDuplicates > 0) {
        setFeedback({
          kind: 'warning',
          title: `No users imported — all ${rowWord(skippedDuplicates)} matched existing emails.`,
        });
      } else {
        setFeedback({ kind: 'success', title: 'Import complete.' });
      }
    } catch (err: any) {
      // Prefer backend validation details (string or array) over Axios's
      // generic "Request failed with status code 4xx" message.
      const serverMsg = err?.response?.data?.message;
      const detail = Array.isArray(serverMsg)
        ? serverMsg.join('; ')
        : serverMsg;
      const message = detail || err?.message || 'CSV import failed.';
      setFeedback({ kind: 'error', title: 'CSV import failed.', details: [message] });
    } finally {
      setIsImporting(false);
      // Allow uploading the same file again after an error/success
      if (importInputRef.current) {
        importInputRef.current.value = '';
      }
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({
      empNo: '',
      fullName: '',
      department: '',
      branch: '',
      password: '',
      email: '',
      telNo: '',
      designation: '',
      role: 'EMPLOYEE',
    });
    setError('');
    setPhoneError('');
    setShowModal(true);
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setFormData({
      empNo: user.empNo,
      fullName: user.fullName,
      department: user.department,
      branch: user.branch,
      password: '',
      email: user.email || '',
      telNo: user.telNo || '',
      designation: user.designation || '',
      role: user.role,
    });
    setError('');
    setPhoneError('');
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setPhoneError('');
    setIsSubmitting(true);

    const normalizedPhone = formData.telNo.trim();

    if (!editingUser && !normalizedPhone) {
      setPhoneError('Phone number is required.');
      setIsSubmitting(false);
      return;
    }

    if (normalizedPhone && !isValidPhoneNumber(normalizedPhone)) {
      setPhoneError('Phone number must be valid Sri Lankan format (e.g. 0742256408).');
      setIsSubmitting(false);
      return;
    }

    if (formData.email && !isValidEmail(formData.email)) {
      setError('Please enter a valid email address.');
      setIsSubmitting(false);
      return;
    }

    try {
      if (editingUser) {
        const roleChanged = formData.role !== editingUser.role;
        if (roleChanged && !isAdminRole(currentUser?.role)) {
          setError('Only an administrator can change a user’s role. Ask an admin or revert the role.');
          setIsSubmitting(false);
          return;
        }

        const updateData: Parameters<typeof usersService.update>[1] = {
          empNo: formData.empNo,
          fullName: formData.fullName,
          department: formData.department,
          branch: formData.branch,
          email: formData.email || undefined,
          telNo: normalizedPhone,
          designation: formData.designation || '',
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await usersService.update(editingUser.id, updateData);
        if (roleChanged && isAdminRole(currentUser?.role)) {
          await usersService.changeRole(editingUser.id, formData.role);
        }
      } else {
        // Create user
        await usersService.create({
          empNo: formData.empNo,
          fullName: formData.fullName,
          department: formData.department,
          branch: formData.branch,
          password: formData.password,
          email: formData.email || undefined,
          telNo: normalizedPhone,
          designation: formData.designation || '',
          role: formData.role,
        });
      }

      setShowModal(false);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (userId: string) => {
    setPendingDeleteUserId(userId);
  };

  const handleModalBackdropMouseDown = (
    e: React.MouseEvent<HTMLDivElement>
  ) => {
    setShouldCloseOnBackdropMouseUp(e.target === e.currentTarget);
  };

  const handleModalBackdropMouseUp = (
    e: React.MouseEvent<HTMLDivElement>,
    onClose: () => void
  ) => {
    if (shouldCloseOnBackdropMouseUp && e.target === e.currentTarget) {
      onClose();
    }
    setShouldCloseOnBackdropMouseUp(false);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteUserId) return;
    setIsDeleting(true);

    try {
      await usersService.delete(pendingDeleteUserId);
      setSelectedUserIds((prev) => {
        if (!prev.has(pendingDeleteUserId)) return prev;
        const next = new Set(prev);
        next.delete(pendingDeleteUserId);
        return next;
      });
      setPendingDeleteUserId(null);
      loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Failed to delete user');
    } finally {
      setIsDeleting(false);
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'badge-admin';
      case 'CO_ADMIN':
        return 'badge-hr';
      case 'CONTENT_UPLOADER':
        return 'badge-content';
      case 'BRANCH_ADMIN':
      case 'DEPARTMENT_ADMIN':
        return 'badge-content';
      default:
        return 'badge-employee';
    }
  };

  // Pagination derived state — `safeCurrentPage` guards against the filtered
  // list shrinking out from under a high page number (e.g. after a delete).
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIdx = (safeCurrentPage - 1) * pageSize;
  const paginatedUsers = filteredUsers.slice(startIdx, startIdx + pageSize);

  // Selection helpers — bulk delete is admin-only and self-delete is blocked.
  const isAdmin = isAdminRole(currentUser?.role);
  const selectableOnPage = paginatedUsers.filter((u) => u.id !== currentUser?.id);
  const allOnPageSelected =
    selectableOnPage.length > 0 &&
    selectableOnPage.every((u) => selectedUserIds.has(u.id));
  const someOnPageSelected = selectableOnPage.some((u) => selectedUserIds.has(u.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate =
        someOnPageSelected && !allOnPageSelected;
    }
  }, [someOnPageSelected, allOnPageSelected]);

  const toggleSelectAllOnPage = () => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        selectableOnPage.forEach((u) => next.delete(u.id));
      } else {
        selectableOnPage.forEach((u) => next.add(u.id));
      }
      return next;
    });
  };

  const toggleSelectUser = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const clearSelection = () => setSelectedUserIds(new Set());

  const handleConfirmBulkDelete = async () => {
    if (selectedUserIds.size === 0) return;
    setIsBulkDeleting(true);

    const ids = Array.from(selectedUserIds);
    let succeeded = 0;
    const failures: string[] = [];

    for (const id of ids) {
      try {
        await usersService.delete(id);
        succeeded++;
      } catch (err) {
        const name = users.find((u) => u.id === id)?.fullName || id;
        failures.push(name);
      }
    }

    setPendingBulkDelete(false);
    setSelectedUserIds(new Set());
    await loadUsers();
    setIsBulkDeleting(false);

    const userWord = (n: number) => `${n} user${n === 1 ? '' : 's'}`;
    if (failures.length === 0) {
      setFeedback({
        kind: 'success',
        title: `Successfully deleted ${userWord(succeeded)}.`,
      });
    } else if (succeeded === 0) {
      setFeedback({
        kind: 'error',
        title: `Failed to delete ${userWord(failures.length)}.`,
        details: failures,
      });
    } else {
      setFeedback({
        kind: 'warning',
        title: `Deleted ${userWord(succeeded)} — failed to delete ${userWord(failures.length)}.`,
        details: failures,
      });
    }
  };

  if (!isCoAdminOrAdmin(currentUser?.role)) {
    return (
      <div className="user-management-page">
        <Header />
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management-page">
      <Header />
      {/* Hamburger for mobile/tablet only */}
      {isMobileOrTablet && (
        <button
          className="hamburger-btn"
          aria-label="Open menu"
          onClick={() => setMobileSidebarOpen(true)}
        >
          <span className="hamburger-line" />
          <span className="hamburger-line" />
          <span className="hamburger-line" />
        </button>
      )}
      <div className="user-management-container">
        <div className="page-header">
          <div className="header-info">
            <h1>User Management</h1>
            <p>Manage employee accounts and permissions</p>
          </div>
          <div className="header-actions">
            <button
              className="btn-import"
              onClick={handleImportClick}
              disabled={isImporting || isLoading}
            >
              {isImporting ? 'Importing...' : 'Import CSV'}
            </button>
            <Button className="btn-create" onClick={openCreateModal}>
              + Add User
            </Button>
          </div>
        </div>

        {showImportSection && (
          <div className="import-upload-section">
            <div className="import-upload-header">
              <strong>Import CSV</strong>
              <button
                type="button"
                className="btn-import"
                onClick={handleDownloadTemplate}
                disabled={isImporting || isLoading}
              >
                Download Template
              </button>
            </div>
            <input
              ref={importInputRef}
              type="file"
              accept=".csv"
              onChange={handleImportCSV}
              disabled={isImporting || isLoading}
            />

            <div className="import-csv-format">
              <h4 className="import-csv-format-title">CSV format</h4>
              <p className="import-csv-format-intro">
                Use UTF-8 encoding. The first row must be column headers. Column order can vary; names are
                matched case-insensitively (spaces and underscores are ignored). Use double quotes around a
                field if it contains commas.
              </p>
              <div className="import-csv-table-wrap">
                <table className="import-csv-table">
                  <thead>
                    <tr>
                      <th>Column</th>
                      <th>Required</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><code>Emp No</code></td>
                      <td>Yes</td>
                      <td>Employee number</td>
                    </tr>
                    <tr>
                      <td><code>Full Name</code></td>
                      <td>Yes</td>
                      <td>Display name</td>
                    </tr>
                    <tr>
                      <td><code>Department</code></td>
                      <td>Yes</td>
                      <td>e.g. IT, HR, SALES</td>
                    </tr>
                    <tr>
                      <td><code>Branch</code></td>
                      <td>Yes</td>
                      <td>Branch name (match your org list)</td>
                    </tr>
                    <tr>
                      <td><code>Password</code></td>
                      <td>Yes</td>
                      <td>Initial password (hashed on save)</td>
                    </tr>
                    <tr>
                      <td><code>Role</code></td>
                      <td>Yes</td>
                      <td>
                        {roles.join(', ')} — <code>HR_ADMIN</code> is accepted as <code>CO_ADMIN</code>
                      </td>
                    </tr>
                    <tr>
                      <td><code>User Email</code></td>
                      <td>No</td>
                      <td>Must be unique; invalid or duplicate emails are skipped</td>
                    </tr>
                    <tr>
                      <td><code>Designation</code></td>
                      <td>No</td>
                      <td>Job title</td>
                    </tr>
                    <tr>
                      <td><code>Mobile</code></td>
                      <td>No</td>
                      <td>Sri Lankan format (e.g. 0741234567)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="import-csv-example-label">Example (header + one row)</p>
              <pre className="import-csv-example">
{`Emp No,Full Name,Department,Branch,Password,Role,User Email,Designation,Mobile
E001,John Doe,IT,Kandy,TempPass123!,EMPLOYEE,john.doe@company.com,Engineer,0741234567`}
              </pre>
            </div>
          </div>
        )}

        {feedback && (
          <div
            className={`feedback-banner feedback-banner--${feedback.kind}`}
            role={feedback.kind === 'error' ? 'alert' : 'status'}
            aria-live={feedback.kind === 'error' ? 'assertive' : 'polite'}
          >
            <div className="feedback-banner__icon" aria-hidden>
              {feedback.kind === 'success' ? '✓' : feedback.kind === 'warning' ? '!' : '×'}
            </div>
            <div className="feedback-banner__content">
              <div className="feedback-banner__title">{feedback.title}</div>
              {feedback.details && feedback.details.length > 0 && (
                <ul className="feedback-banner__details">
                  {feedback.details.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ul>
              )}
            </div>
            <button
              type="button"
              className="feedback-banner__close"
              aria-label="Dismiss"
              onClick={() => setFeedback(null)}
            >
              ×
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="filters-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by name or employee no..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <Combobox
              className="um-select-wrap"
              ariaLabel="Filter by department"
              value={filterDepartment}
              onChange={(v) => setFilterDepartment(v)}
              options={[
                { value: 'ALL', label: 'All Departments' },
                ...departments.map((dept) => ({ value: dept, label: dept })),
              ]}
            />
            <Combobox
              className="um-select-wrap"
              ariaLabel="Filter by role"
              value={filterRole}
              onChange={(v) => setFilterRole(v)}
              options={[
                { value: 'ALL', label: 'All Roles' },
                ...roles.map((role) => ({ value: role, label: formatRoleLabel(role) })),
              ]}
            />
          </div>
        </div>

        {/* Bulk action bar — only visible when admin has selected ≥1 user. */}
        {isAdmin && selectedUserIds.size > 0 && (
          <div className="bulk-action-bar">
            <div className="bulk-action-count">
              {selectedUserIds.size} user{selectedUserIds.size === 1 ? '' : 's'} selected
            </div>
            <div className="bulk-action-buttons">
              <button
                type="button"
                className="btn-bulk-clear"
                onClick={clearSelection}
                disabled={isBulkDeleting}
              >
                Clear
              </button>
              <button
                type="button"
                className="btn-bulk-delete"
                onClick={() => setPendingBulkDelete(true)}
                disabled={isBulkDeleting}
              >
                Delete selected
              </button>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="users-table-container">
          {isLoading ? (
            <div className="loading">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="no-users">No users found</div>
          ) : (
            <table className="users-table">
              <thead>
                <tr>
                  {isAdmin && (
                    <th className="users-table-select-col">
                      <input
                        ref={headerCheckboxRef}
                        type="checkbox"
                        aria-label="Select all users on this page"
                        checked={allOnPageSelected}
                        onChange={toggleSelectAllOnPage}
                        disabled={selectableOnPage.length === 0}
                      />
                    </th>
                  )}
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Branch</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((user) => {
                  const isSelf = user.id === currentUser?.id;
                  const isRowSelected = selectedUserIds.has(user.id);
                  return (
                    <tr key={user.id} className={isRowSelected ? 'row-selected' : ''}>
                      {isAdmin && (
                        <td className="users-table-select-col">
                          <input
                            type="checkbox"
                            aria-label={`Select ${user.fullName}`}
                            checked={isRowSelected}
                            onChange={() => toggleSelectUser(user.id)}
                            disabled={isSelf}
                            title={
                              isSelf ? 'You cannot delete your own account' : undefined
                            }
                          />
                        </td>
                      )}
                      <td>
                        <div className="user-info">
                          <div className="user-avatar">
                            {user.profilePicture ? (
                              <img src={user.profilePicture} alt={user.fullName} />
                            ) : (
                              user.fullName.charAt(0)
                            )}
                          </div>
                          <div>
                            <div className="user-name">{user.fullName}</div>
                            <div className="user-empno">{user.empNo}</div>
                          </div>
                        </div>
                      </td>
                      <td>{user.department}</td>
                      <td>{user.branch}</td>
                      <td>
                        <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                          {formatRoleLabel(user.role)}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-edit"
                            onClick={() => openEditModal(user)}
                          >
                            Edit
                          </button>
                          {isAdmin && !isSelf && (
                            <button
                              className="btn-delete"
                              onClick={() => handleDelete(user.id)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination footer */}
        <div className="table-footer">
          <div className="table-footer-info">
            {filteredUsers.length === 0
              ? 'No users to show'
              : `Showing ${startIdx + 1}–${Math.min(
                  startIdx + pageSize,
                  filteredUsers.length,
                )} of ${filteredUsers.length}`}
          </div>
          <div className="table-footer-pagination">
            <label className="page-size-label">
              Per page:
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>
            <div className="pagination-controls">
              <button
                type="button"
                className="btn-page"
                disabled={safeCurrentPage <= 1}
                onClick={() => setCurrentPage(1)}
                aria-label="First page"
              >
                «
              </button>
              <button
                type="button"
                className="btn-page"
                disabled={safeCurrentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                ‹
              </button>
              <span className="page-indicator">
                Page {safeCurrentPage} of {totalPages}
              </span>
              <button
                type="button"
                className="btn-page"
                disabled={safeCurrentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                aria-label="Next page"
              >
                ›
              </button>
              <button
                type="button"
                className="btn-page"
                disabled={safeCurrentPage >= totalPages}
                onClick={() => setCurrentPage(totalPages)}
                aria-label="Last page"
              >
                »
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Edit User' : 'Create User'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="error-message">{error}</div>}

                <div className="form-row">
                  <div className="form-group">
                    <label>Employee No *</label>
                    <input
                      type="text"
                      value={formData.empNo}
                      onChange={(e) => setFormData({ ...formData, empNo: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Department *</label>
                    <Combobox
                      ariaLabel="Department"
                      value={formData.department}
                      onChange={(v) => setFormData({ ...formData, department: v })}
                      required
                      placeholder="Select"
                      options={departments.map((dept) => ({ value: dept, label: dept }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Branch *</label>
                    <Combobox
                      ariaLabel="Branch"
                      value={formData.branch}
                      onChange={(v) => setFormData({ ...formData, branch: v })}
                      required
                      placeholder="Select"
                      options={branches.map((b) => ({ value: b, label: b }))}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="name@example.com"
                    required
                  />
                  <div className="field-hint">Optional, not used for login</div>
                  </div>
                  <div className="form-group">
                    <label>Role *</label>
                    <Combobox
                      ariaLabel="Role"
                      value={formData.role}
                      onChange={(v) =>
                        setFormData({ ...formData, role: v as User['role'] })
                      }
                      required
                      disabled={!!editingUser && !isAdminRole(currentUser?.role)}
                      options={roles.map((role) => ({
                        value: role,
                        label: formatRoleLabel(role),
                      }))}
                    />
                    {editingUser && !isAdminRole(currentUser?.role) && (
                      <div className="field-hint">
                        Only a full administrator can change another user’s role when editing.
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Phone{!editingUser ? ' *' : ''}</label>
                    <input
                      type="tel"
                      value={formData.telNo}
                      onChange={(e) => {
                        setFormData({ ...formData, telNo: e.target.value });
                        if (phoneError) setPhoneError('');
                      }}
                      required={!editingUser}
                      inputMode="numeric"
                      placeholder="0771234567"
                    />
                    {phoneError && <div className="field-error">{phoneError}</div>}
                  </div>

                  <div className="form-group">
                    <label>Designation</label>
                    <input
                      type="text"
                      value={formData.designation}
                      onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                      placeholder="Senior Executive"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{editingUser ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                    placeholder={editingUser ? 'Leave blank to keep current' : 'Min 6 characters'}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : (editingUser ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pendingDeleteUserId && (
        <div
          className="modal-overlay"
          onMouseDown={handleModalBackdropMouseDown}
          onMouseUp={(e) =>
            handleModalBackdropMouseUp(e, () => {
              if (!isDeleting) setPendingDeleteUserId(null);
            })
          }
        >
          <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
            </div>
            <div className="modal-body">
              <p className="delete-confirm-message">Are you sure you want to delete this user?</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setPendingDeleteUserId(null)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-submit"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingBulkDelete && (
        <div
          className="modal-overlay"
          onMouseDown={handleModalBackdropMouseDown}
          onMouseUp={(e) =>
            handleModalBackdropMouseUp(e, () => {
              if (!isBulkDeleting) setPendingBulkDelete(false);
            })
          }
        >
          <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                Delete {selectedUserIds.size} user
                {selectedUserIds.size === 1 ? '' : 's'}?
              </h2>
            </div>
            <div className="modal-body">
              <p className="delete-confirm-message">
                This will permanently delete {selectedUserIds.size} user
                {selectedUserIds.size === 1 ? '' : 's'} and all of their data. This
                action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setPendingBulkDelete(false)}
                disabled={isBulkDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-submit"
                onClick={handleConfirmBulkDelete}
                disabled={isBulkDeleting}
              >
                {isBulkDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isMobileOrTablet && mobileSidebarOpen && (
        <MobileSidebar onClose={() => setMobileSidebarOpen(false)} />
      )}
    </div>
  );
};

export default UserManagement;
